import React, { useState, useEffect, useCallback } from "react";
import { Navigation } from "../components/website/navigation";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import axios from "axios";
import './css/Subscription.css';
import { API_ENDPOINTS } from "../config/apiConfig";
import { useNavigate } from "react-router-dom";

// Zuzhao's API Key
// const stripePromise = loadStripe("pk_test_51RBGXyRvSZGBetSdltTBNF3ES2Mvhip7x8gQ7snK6xUSHzs1vj6njHx2dAmD4srCpcp40z9f9W2ZaDrwgAntiLcl00enUNq5Ia"); 

// Jonathan's API Key
const stripePromise = loadStripe("pk_test_51RAGlNIoPZSqfl8FfQ3PpoLAeBEiCQlbWuiCuTOARQBSKXhxPsZhI4eA9feEDIyJrz0YSeuBXS5auzloF4gZ4efo006T6QmZ27");

function Subscription() {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // This would typically come from your API
    const availablePlans = [
      {
        id: "basic",
        name: "Basic Plan",
        price: "Free",
        stripe_price_id: "price_1RCeX0IoPZSqfl8FtUb9rPow", // Add your actual Stripe price ID here
        features: [
          "10 fleet planning sessions per month",
          "Basic route optimization",
          "Weather alerts",
          "Email support",
          "Single user license"
        ]
      },
      {
        id: "premium",
        name: "Premium Plan",
        price: 49.99,
        stripe_price_id: "price_1RC49BIoPZSqfl8FZmKxkyr2", // Add your actual Stripe price ID here
        features: [
          "Unlimited fleet planning sessions",
          "Advanced route optimization",
          "Real-time weather integration",
          "Fuel consumption analytics",
          "24/7 priority support",
          "Team collaboration tools"
        ]
      },
      {
        id: "enterprise",
        name: "Enterprise Plan",
        price: 99.99,
        stripe_price_id: "price_1RCeXlIoPZSqfl8FqpsHmxmL", // Add your actual Stripe price ID here
        features: [
          "All Premium features",
          "Custom API integration",
          "Dedicated account manager",
          "Custom reporting",
          "White-label options",
          "Unlimited team members"
        ]
      }
    ];

    setPlans(availablePlans);
    setSelectedPlan(null); // Don't select a default plan
    setLoading(false);
  }, []);

  const handleSubscribe = () => {
    if (!selectedPlan) return;
    setShowCheckout(true);
  };

  const handleBackToPlans = () => {
    setShowCheckout(false);
    setError(null);
  };

  // COMMENT THIS OUT WHEN THE SUBSCRIPTION IS READY TO BE LIVE
  const handleSelectedPlan = async (plan) => {
    setSelectedPlan(plan);
    if (plan.id === "basic") {
      console.log(localStorage.getItem('authToken'));

      try {
        // Correct way to pass headers to axios
        const response = await axios.post(
          API_ENDPOINTS.freeSubscribe,
          {}, // Empty object for request body (or add data if needed)
          {
            headers: {
              'Authorization': `Token ${localStorage.getItem('authToken')}`,
              'Content-Type': 'application/json'
            }
          }
        );

        console.log(response);
        navigate('/profile');
      } catch (error) {
        console.log("Error:", error);
        setError("Please login to continue");
        if (error.response) {
          console.log("Response data:", error.response.data);
          console.log("Response status:", error.response.status);
        }
      }
    } else if (plan.id === "premium") {
      navigate('/construction');
    } else if (plan.id === "enterprise") {
      navigate('/construction');
    }
  };

  const fetchClientSecret = useCallback(async () => {
    try {
      console.log("Sending request to:", API_ENDPOINTS.subscribe);

      const response = await axios.post(API_ENDPOINTS.subscribe, {
        price_id: selectedPlan.stripe_price_id,
        subscription_type: selectedPlan.id
      }, {
        headers: {
          'Authorization': `Token ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });

      console.log("Received response:", response.data);

      // Check for client_secret (snake_case) or clientSecret (camelCase)
      const clientSecret = response.data.clientSecret || response.data.client_secret;

      if (!clientSecret) {
        console.error("No client secret in response:", response.data);
        throw new Error("Server response missing client secret");
      }

      return clientSecret;
    } catch (err) {
      console.error("Error details:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });

      let errorMessage = "Failed to create checkout session";
      if (err.response) {
        errorMessage = err.response.data?.error ||
          `Server error: ${err.response.status}`;
      } else if (err.request) {
        errorMessage = "No response from server. Please check your connection.";
      }

      setError(errorMessage);
      setShowCheckout(false);
      throw new Error(errorMessage);
    }
  }, [selectedPlan]);

  const options = { fetchClientSecret };

  if (loading) {
    return <div className="loading">Loading subscription plans...</div>;
  }

  return (
    <div className="subscription-page">
      <Navigation />
      {error && (
        <div className="error-overlay">
          <div className="error-overlay-content">
            <span>{error}</span>
            <button
              onClick={() => setError('')}
              className="error-overlay-close-btn"
            >
              &times;
            </button>
          </div>
        </div>
      )}
      <div className="subscription-container">
        <h1 className="subscription-title">Choose Your Subscription Plan</h1>

        {!showCheckout ? (
          // Plan selection view (full width)
          <div className="plan-selection-container">
            <div className="plan-cards">
              {plans.map(plan => (
                <div
                  key={plan.id}
                  className={`plan-card ${selectedPlan?.id === plan.id ? 'selected' : ''}`}
                  onClick={() => setSelectedPlan(plan)}
                >
                  <div className="plan-header">
                    <h2>{plan.name}</h2>
                    <div className="plan-price">{plan.price === "Free" ? "Free" : "$" + plan.price}<span>{plan.price === "Free" ? "" : "/month"}</span></div>
                  </div>

                  <div className="plan-features">
                    <ul>
                      {plan.features.map((feature, index) => (
                        <li key={index}>✓ {feature}</li>
                      ))}
                    </ul>
                  </div>


                  {/* UNCOMMENT THIS WHEN THE SUBSCRIPTION IS READY TO BE LIVE */}
                  {/* <button 
                    className={`select-plan-button ${selectedPlan?.id === plan.id ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPlan(plan);
                      setShowCheckout(true);
                    }}
                  >
                    // Comment this out {selectedPlan?.id === plan.id ? 'Selected' : 'Select Plan'} 
                    Select Plan
                  </button> */}

                  <button
                    className={`select-plan-button ${selectedPlan?.id === plan.id ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPlan(plan);
                      handleSelectedPlan(plan);
                    }}
                  >
                    Select Plan
                  </button>
                </div>
              ))}
              <div className="plan-card">
                <div className="plan-content">
                  <h2>Looking for a tailored solution?</h2>
                  <p>
                    If you don't see a plan that perfectly fits your needs, please don't hesitate to contact us.
                    Send us an email at <span className="email-link">info@amptrans.net</span> and 
                    we can provide consulting and create a custom plan that matches your specific requirements.
                  </p>
                </div>
              </div>
            </div>

            {/* {selectedPlan && (
              <div className="plan-action">
                <button 
                  onClick={handleSubscribe}
                  className="subscribe-button"
                >
                  Continue with {selectedPlan.name}
                </button>
              </div>
            )} */}
          </div>
        ) : (
          // Checkout view (full width)
          <div className="checkout-container">
            <div className="checkout-header">
              <h2>Complete Your Subscription</h2>
              <div className="selected-plan-summary">
                <span className="plan-name">{selectedPlan.name}</span>
                <span className="plan-price">${selectedPlan.price}/month</span>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="embedded-checkout-wrapper">
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={options}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>

            <button
              onClick={handleBackToPlans}
              className="back-button"
            >
              ← Back to Plans
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Subscription;